import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, AlertTriangle, PackagePlus, Upload, FlaskConical, ChefHat } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

export default function Inventory() {
  const { currentOutlet } = useAuth();
  const [ings, setIngs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', unit: 'kg', stock: 0, reorder_level: 0, cost: 0 });
  const [adjust, setAdjust] = useState(null);
  const [adjForm, setAdjForm] = useState({ delta: 0, reason: 'received' });
  const [produce, setProduce] = useState(null);
  const [batches, setBatches] = useState(1);
  const [viewSub, setViewSub] = useState(null);
  const [editIng, setEditIng] = useState(null);
  const [editForm, setEditForm] = useState({ cost: 0, reorder_level: 0, unit: 'g' });
  const [waste, setWaste] = useState(null);
  const [wasteForm, setWasteForm] = useState({ qty: 0, reason: 'wastage' });
  const [moves, setMoves] = useState([]);
  const subFileRef = useRef();
  const recipeFileRef = useRef();

  const load = async () => {
    const [ing, sr, mv] = await Promise.all([api.get('/ingredients'), api.get('/sub-recipes'), api.get('/stock-movements')]);
    setIngs(ing.data); setSubs(sr.data); setMoves(mv.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => { await api.post('/ingredients', { ...form, outlet_id: currentOutlet.id, stock: Number(form.stock), reorder_level: Number(form.reorder_level), cost: Number(form.cost) }); setOpen(false); setForm({ name: '', unit: 'kg', stock: 0, reorder_level: 0, cost: 0 }); load(); toast.success('Added'); };
  const doAdjust = async () => { await api.post(`/ingredients/${adjust.id}/adjust`, { delta: Number(adjForm.delta), reason: adjForm.reason }); setAdjust(null); setAdjForm({ delta: 0, reason: 'received' }); load(); toast.success('Stock updated'); };
  const doProduce = async () => { const { data } = await api.post(`/sub-recipes/${produce.id}/produce`, { batches: Number(batches) }); setProduce(null); setBatches(1); load(); toast.success(`Produced. New stock: ${data.stock} ${produce.yield_unit}`); };
  const openEditIng = (i) => { setEditIng(i); setEditForm({ cost: i.cost || 0, reorder_level: i.reorder_level || 0, unit: i.unit || 'g' }); };
  const saveEditIng = async () => { await api.put(`/ingredients/${editIng.id}`, { cost: Number(editForm.cost), reorder_level: Number(editForm.reorder_level), unit: editForm.unit }); setEditIng(null); load(); toast.success('Ingredient updated'); };
  const doWaste = async () => { await api.post(`/sub-recipes/${waste.id}/waste`, { qty: Number(wasteForm.qty), reason: wasteForm.reason }); setWaste(null); setWasteForm({ qty: 0, reason: 'wastage' }); load(); toast.success('Wastage logged'); };

  const uploadFile = async (file, url, label) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.matched_count !== undefined) toast.success(`Recipes: ${data.matched_count} matched, ${data.cyo_count} build-your-own, ${data.unmatched_count} unmatched`);
      else toast.success(`${label}: ${data.created ?? data.sub_recipes} imported`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `${label} import failed`); }
  };

  const lowCount = ings.filter((i) => i.stock <= i.reorder_level).length;
  const value = ings.reduce((s, i) => s + i.stock * i.cost, 0);
  const subLow = subs.filter((s) => s.stock <= (s.reorder_level || 0)).length;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHelp id="inventory">Track raw ingredient stock and prepped <b>sub-recipes</b> (sauces, doughs). Selling a dish auto-deducts its recipe — raw ingredients and/or sub-recipe stock. Use <b>Produce</b> to make a batch of a sub-recipe (deducts raw ingredients, adds prepped stock). Import recipes from the client's Excel files.</PageHelp>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Inventory & Recipes</h1><p className="text-sm text-muted-foreground">{ings.length} ingredients · {lowCount} low · {subs.length} sub-recipes · Stock value {fmtAED(value)}</p></div>
        <div className="flex gap-2 flex-wrap">
          <input ref={subFileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="import-subrecipes-input" onChange={(e) => { uploadFile(e.target.files?.[0], '/sub-recipes/import?replace=true', 'Sub-recipes'); e.target.value = ''; }} />
          <input ref={recipeFileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="import-recipes-input" onChange={(e) => { uploadFile(e.target.files?.[0], '/menu/recipes/import?replace=false', 'Recipes'); e.target.value = ''; }} />
          <Button variant="outline" data-testid="import-subrecipes-button" onClick={() => subFileRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> Import Sub-Recipes</Button>
          <Button variant="outline" data-testid="import-recipes-button" onClick={() => recipeFileRef.current?.click()}><ChefHat className="h-4 w-4 mr-1" /> Import Recipes</Button>
          <Button data-testid="add-ingredient-button" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Ingredient</Button>
        </div>
      </div>

      <Tabs defaultValue="ingredients">
        <TabsList>
          <TabsTrigger value="ingredients" data-testid="tab-ingredients">Ingredients ({ings.length})</TabsTrigger>
          <TabsTrigger value="subrecipes" data-testid="tab-subrecipes">Sub-Recipes ({subs.length})</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="ingredients">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Ingredient</TableHead><TableHead>Stock</TableHead><TableHead>Reorder</TableHead><TableHead className="text-right">Cost/Unit</TableHead><TableHead></TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {ings.map((i) => {
                  const low = i.stock <= i.reorder_level;
                  return (
                    <TableRow key={i.id} data-testid={`ingredient-row-${i.name}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="font-mono">{i.stock} {i.unit}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{i.reorder_level} {i.unit}</TableCell>
                      <TableCell className="text-right font-mono">{fmtAED(i.cost)}</TableCell>
                      <TableCell>{low && <Badge variant="outline" className="text-destructive border-destructive/40"><AlertTriangle className="h-3 w-3 mr-1" />Low</Badge>}</TableCell>
                      <TableCell><Button size="sm" variant="outline" data-testid={`adjust-${i.name}`} onClick={() => setAdjust(i)}><PackagePlus className="h-3.5 w-3.5 mr-1" />Adjust</Button> <Button size="sm" variant="ghost" data-testid={`edit-ing-${i.name}`} onClick={() => openEditIng(i)}>Edit</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="subrecipes">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Sub-Recipe</TableHead><TableHead>Prepped Stock</TableHead><TableHead>Batch Yield</TableHead><TableHead>Ingredients</TableHead><TableHead></TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {subs.map((s) => {
                  const low = s.stock <= (s.reorder_level || 0);
                  return (
                    <TableRow key={s.id} data-testid={`subrecipe-row-${s.name}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="font-mono">{s.stock} {s.yield_unit}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{s.yield_qty} {s.yield_unit}</TableCell>
                      <TableCell><Button size="sm" variant="ghost" onClick={() => setViewSub(s)}>{s.lines?.length || 0} items</Button></TableCell>
                      <TableCell>{low && <Badge variant="outline" className="text-destructive border-destructive/40"><AlertTriangle className="h-3 w-3 mr-1" />Low</Badge>}</TableCell>
                      <TableCell><Button size="sm" variant="outline" data-testid={`produce-${s.name}`} onClick={() => { setProduce(s); setBatches(1); }}><FlaskConical className="h-3.5 w-3.5 mr-1" />Produce</Button> <Button size="sm" variant="ghost" className="text-destructive" data-testid={`waste-${s.name}`} onClick={() => { setWaste(s); setWasteForm({ qty: 0, reason: 'wastage' }); }}>Waste</Button></TableCell>
                    </TableRow>
                  );
                })}
                {subs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No sub-recipes yet. Use <b>Import Sub-Recipes</b> to load them.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="log">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Item</TableHead><TableHead>Type</TableHead><TableHead>Change</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody>
                {moves.map((m) => (
                  <TableRow key={m.id} data-testid={`movement-row`}>
                    <TableCell className="text-xs text-muted-foreground">{new Date(m.at).toLocaleString()}</TableCell>
                    <TableCell className="font-medium">{m.item_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] capitalize">{m.item_type}</Badge></TableCell>
                    <TableCell className={`font-mono ${m.delta < 0 ? 'text-destructive' : 'text-success'}`}>{m.delta > 0 ? '+' : ''}{m.delta}</TableCell>
                    <TableCell className="capitalize text-sm">{m.reason}</TableCell>
                  </TableRow>
                ))}
                {moves.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No activity yet. Producing batches, wastage and stock adjustments appear here.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>        <DialogContent><DialogHeader><DialogTitle>Add Ingredient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="ing-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Unit</Label><select className="w-full h-9 rounded-md border bg-background px-2" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>{['kg', 'g', 'L', 'ml', 'pcs'].map((u) => <option key={u}>{u}</option>)}</select></div>
              <div><Label>Cost/Unit</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
              <div><Label>Opening Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
              <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></div>
            </div>
            <Button data-testid="ing-save" className="w-full" onClick={add}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="max-w-xs"><DialogHeader><DialogTitle>Adjust {adjust?.name}</DialogTitle></DialogHeader>
          <div><Label>Change (+/-)</Label><Input data-testid="adj-delta" type="number" value={adjForm.delta} onChange={(e) => setAdjForm({ ...adjForm, delta: e.target.value })} /></div>
          <div><Label>Reason</Label><select className="w-full h-9 rounded-md border bg-background px-2" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })}>{['received', 'wastage', 'spoilage', 'correction', 'transfer'].map((r) => <option key={r}>{r}</option>)}</select></div>
          <Button data-testid="adj-save" onClick={doAdjust}>Apply</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!produce} onOpenChange={(o) => !o && setProduce(null)}>
        <DialogContent className="max-w-xs"><DialogHeader><DialogTitle>Produce {produce?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Each batch yields <b>{produce?.yield_qty} {produce?.yield_unit}</b> and deducts its raw ingredients from stock.</p>
          <div><Label>Batches</Label><Input data-testid="produce-batches" type="number" min="0.1" step="0.1" value={batches} onChange={(e) => setBatches(e.target.value)} /></div>
          <Button data-testid="produce-confirm" onClick={doProduce}>Produce Batch</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewSub} onOpenChange={(o) => !o && setViewSub(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle>{viewSub?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Batch yield {viewSub?.yield_qty} {viewSub?.yield_unit}</p>
          <div className="space-y-1">
            {(viewSub?.lines || []).map((l, i) => (
              <div key={i} className="flex justify-between text-sm border-b py-1"><span>{l.name}</span><span className="font-mono text-muted-foreground">{l.qty} {l.unit}</span></div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editIng} onOpenChange={(o) => !o && setEditIng(null)}>
        <DialogContent className="max-w-xs"><DialogHeader><DialogTitle>Edit {editIng?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Unit</Label><select className="w-full h-9 rounded-md border bg-background px-2" value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}>{['kg', 'g', 'L', 'ml', 'pcs'].map((u) => <option key={u}>{u}</option>)}</select></div>
            <div><Label>Cost / {editForm.unit}</Label><Input data-testid="edit-ing-cost" type="number" step="0.001" value={editForm.cost} onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })} /></div>
            <div className="col-span-2"><Label>Reorder Level</Label><Input data-testid="edit-ing-reorder" type="number" value={editForm.reorder_level} onChange={(e) => setEditForm({ ...editForm, reorder_level: e.target.value })} /></div>
          </div>
          <Button data-testid="edit-ing-save" className="mt-2" onClick={saveEditIng}>Save</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!waste} onOpenChange={(o) => !o && setWaste(null)}>
        <DialogContent className="max-w-xs"><DialogHeader><DialogTitle>Log Wastage · {waste?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Current stock: <b>{waste?.stock} {waste?.yield_unit}</b></p>
          <div><Label>Quantity wasted ({waste?.yield_unit})</Label><Input data-testid="waste-qty" type="number" value={wasteForm.qty} onChange={(e) => setWasteForm({ ...wasteForm, qty: e.target.value })} /></div>
          <div><Label>Reason</Label><select className="w-full h-9 rounded-md border bg-background px-2" value={wasteForm.reason} onChange={(e) => setWasteForm({ ...wasteForm, reason: e.target.value })}>{['wastage', 'spoilage', 'expired', 'spillage', 'correction'].map((r) => <option key={r}>{r}</option>)}</select></div>
          <Button data-testid="waste-confirm" className="mt-2" onClick={doWaste}>Log Wastage</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
